import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CreditsPackage } from 'types/interfaces/CreditsPackage';
import { Package } from 'types/interfaces/Package';

import { PackagesThemeGroups } from 'api/AuthApi';
import { saveUpsalesIdsToSessionStorage } from 'helpers/payment/upsales';

type PaymentState = {
  upgradePackages: Package[];
  upgradePackagesCreditsGifts: Record<string, number>;
  upsalePackages: Package[];
  vipPackages: Package[];
  microPackages: Package[];
  creditsPackages: CreditsPackage[];
  stopExitPackage: Package[];
  credits: {
    amount: number;
    currency: string;
  };
  theme: PackagesThemeGroups;
  vipDiscountAvailable: boolean;
  vipPaymentSuccess: boolean;
  downgradeAvailable: boolean;
};

const initialState: PaymentState = {
  upgradePackages: [],
  upgradePackagesCreditsGifts: {},
  upsalePackages: [],
  vipPackages: [],
  microPackages: [],
  creditsPackages: [],
  stopExitPackage: [],
  credits: {
    amount: 0,
    currency: 'credits',
  },
  theme: PackagesThemeGroups.Default,
  vipDiscountAvailable: false,
  vipPaymentSuccess: false,
  downgradeAvailable: false,
};

type Predicate<T> = (arg: T) => boolean;

const setActiveIf =
  (predicate: Predicate<Package>): ((pack: Package) => Package) =>
  (pack) =>
    predicate(pack) ? { ...pack, active: true } : { ...pack, active: false };

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setUpgradePackages(state, action: PayloadAction<Package[]>) {
      state.upgradePackages = action.payload;
    },
    setUpgradePackageActive(state, action: PayloadAction<string>) {
      const { payload: id } = action;

      state.upgradePackages = state.upgradePackages.map(
        setActiveIf((pack) => pack.id === id)
      );
    },
    restoreDefaultUpgradePackage(state) {
      state.upgradePackages = state.upgradePackages.map(
        setActiveIf((pack) => pack.is_default)
      );
    },
    setCredits(
      state,
      action: PayloadAction<{
        amount: number;
        currency: string;
      }>
    ) {
      state.credits = action.payload;
    },
    setUpsalePackages(state, action: PayloadAction<Package[]>) {
      state.upsalePackages = action.payload;
    },
    toggleUpsalePackage(state, action: PayloadAction<string>) {
      const { payload: id } = action;

      state.upsalePackages = state.upsalePackages.map((pack) =>
        pack.id === id
          ? {
              ...pack,
              active: !pack.active,
            }
          : pack
      );
    },
    restoreDefaultUpsalePackages(state) {
      state.upsalePackages = state.upsalePackages.map((pack) => ({
        ...pack,
        active: true,
      }));

      saveUpsalesIdsToSessionStorage(
        state.upsalePackages
          .filter((upsalePack) => upsalePack.is_default)
          .map((upsalePack) => upsalePack.id)
      );
    },
    setVipPackages(state, action: PayloadAction<Package[]>) {
      state.vipPackages = action.payload;
    },
    setMicroPackages(state, action: PayloadAction<Package[]>) {
      state.microPackages = action.payload;
    },
    setCreditsPackages(state, action: PayloadAction<CreditsPackage[]>) {
      state.creditsPackages = action.payload;
    },
    setStopExitPackages(state, action: PayloadAction<Package[]>) {
      state.stopExitPackage = action.payload;
    },
    setPackagesTheme(state, action: PayloadAction<PackagesThemeGroups>) {
      state.theme = action.payload;
    },
    setVipDiscountAvailable(state, action: PayloadAction<boolean>) {
      state.vipDiscountAvailable = action.payload;
    },
    setCreditsAmount(state, action: PayloadAction<number>) {
      state.credits.amount = action.payload;
    },
    addCreditsAmount(state, action: PayloadAction<number>) {
      state.credits.amount += action.payload;
    },
    setVipPaymentSuccess(state) {
      state.vipPaymentSuccess = true;
    },
    setDowngradeAvailable(state, action: PayloadAction<boolean>) {
      state.downgradeAvailable = action.payload;
    },
  },
});

export const {
  setUpgradePackages,
  setCredits,
  setUpsalePackages,
  setUpgradePackageActive,
  toggleUpsalePackage,
  restoreDefaultUpsalePackages,
  restoreDefaultUpgradePackage,
  setVipPackages,
  setMicroPackages,
  setCreditsPackages,
  setStopExitPackages,
  setPackagesTheme,
  setVipDiscountAvailable,
  setCreditsAmount,
  addCreditsAmount,
  setVipPaymentSuccess,
  setDowngradeAvailable,
} = paymentSlice.actions;

export const payment = paymentSlice.reducer;
